import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from './components/layout/ScrollToTop';
import MaintenanceChecker from './components/MaintenanceChecker';
import ErrorBoundary from './components/ErrorBoundary';
import HeaderModern from './components/layout/HeaderModern';
import FooterModern from './components/layout/FooterModern';
import Home from './pages/Home';
import Maintenance from './pages/Maintenance';
import About from './pages/About';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Services from './pages/Services';
import Training from './pages/Training';
import Blog from './pages/Blog';
import BlogArticle from './pages/BlogArticle';
import Contact from './pages/Contact';
import Careers from './pages/Careers';
import Stations from './pages/Stations';
import Resources from './pages/Resources';
import PayAsYouGo from './pages/PayAsYouGo';
import ProxyPower from './pages/ProxyPower';
import EmissionCalculator from './pages/EmissionCalculator';
import Invest from './pages/Invest';
import NotFound from './pages/NotFound';
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import AdminProducts from './pages/admin/Products';
import AdminBlog from './pages/admin/Blog';
import AdminLocations from './pages/admin/Locations';
import AdminPartners from './pages/admin/Partners';
import AdminTestimonials from './pages/admin/Testimonials';
import AdminSettings from './pages/admin/Settings';
import ProductForm from './pages/admin/ProductForm';
import BlogForm from './pages/admin/BlogForm';
import LocationForm from './pages/admin/LocationForm';
import PartnerForm from './pages/admin/PartnerForm';
import TestimonialForm from './pages/admin/TestimonialForm';
import PageEditor from './pages/admin/PageEditor';
import ContentDashboard from './pages/admin/ContentDashboard';
import LegalPage from './pages/LegalPage';
import AdminLegalPages from './pages/admin/LegalPages';
import LegalPageForm from './pages/admin/LegalPageForm';
import FundraisingCampaign from './pages/admin/FundraisingCampaign';
import InvestorLeads from './pages/admin/InvestorLeads';

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <ScrollToTop />
          <Routes>
          {/* Maintenance Route - Always accessible */}
          <Route path="/maintenance" element={<Maintenance />} />

          {/* Admin Routes - Not affected by maintenance */}
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin" element={<Dashboard />}>
            <Route path="dashboard" element={null} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/edit/:id" element={<ProductForm />} />
            
            <Route path="blog" element={<AdminBlog />} />
            <Route path="blog/new" element={<BlogForm />} />
            <Route path="blog/edit/:id" element={<BlogForm />} />
            
            <Route path="locations" element={<AdminLocations />} />
            <Route path="locations/new" element={<LocationForm />} />
            <Route path="locations/edit/:id" element={<LocationForm />} />
            
            <Route path="partners" element={<AdminPartners />} />
            <Route path="partners/new" element={<PartnerForm />} />
            <Route path="partners/edit/:id" element={<PartnerForm />} />
            
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="testimonials/new" element={<TestimonialForm />} />
            <Route path="testimonials/edit/:id" element={<TestimonialForm />} />

            <Route path="legal-pages" element={<AdminLegalPages />} />
            <Route path="legal-pages/:id" element={<LegalPageForm />} />

            <Route path="fundraising" element={<FundraisingCampaign />} />
            <Route path="investor-leads" element={<InvestorLeads />} />

            <Route path="settings" element={<AdminSettings />} />
            
            {/* Page Editor Routes */}
            <Route path="pages/:page" element={<PageEditor />} />
            
            {/* Content Management Routes */}
            <Route path="content/:section" element={<ContentDashboard />} />
          </Route>
          
          {/* Public Routes - Wrapped with MaintenanceChecker */}
          <Route path="/" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main id="main-content" className="flex-grow">
                <Home />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/about" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <About />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/products" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <Products />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/products/:slug" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <ProductDetail />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/services" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <Services />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/training" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <Training />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/blog" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <Blog />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/blog/:slug" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <BlogArticle />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/contact" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <Contact />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/careers" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <Careers />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/stations" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <Stations />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/resources" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <Resources />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/pay-as-you-go" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <PayAsYouGo />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/proxy-power" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <ProxyPower />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/emission-calculator" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <EmissionCalculator />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/invest" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <Invest />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/legal" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <LegalPage />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/privacy" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <LegalPage />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/terms" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <LegalPage />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="/cookies" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <LegalPage />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
          <Route path="*" element={
            <MaintenanceChecker>
              <HeaderModern />
              <main className="flex-grow">
                <NotFound />
              </main>
              <FooterModern />
            </MaintenanceChecker>
          } />
        </Routes>
      </Router>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;